import {
  PendingJointAcatTransferStatus,
  useAuthorizeJointAcatTransfer,
  useGetPendingJointAcatTransfers,
  useRejectJointAcatTransfer,
  useWealthOverview,
} from '@egw/api'
import { Button, ButtonVariant, ModalDrawer, PageHeader } from '@egw/components'
import { configClient } from '@egw/config'
import { WEALTH_ROUTES } from '@egw/constants'
import { Trans, useTranslation } from '@egw/i18n'
import { log } from '@egw/logger'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { FC, useEffect, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate, useParams } from 'react-router'
import { JointAcatTransferConfirmation, JointAcatTransferConfirmType, TransferReviewSection } from '../../components'
import { type Config } from '../../types'

export const JointAccountTransferReview: FC = () => {
  const { t } = useTranslation('features-transfer')

  const navigate = useNavigate()

  const config = configClient.getConfig() as Config

  const [state, setState] = useState<PendingJointAcatTransferStatus>(PendingJointAcatTransferStatus.REQUIRES_REVIEW)

  const [openModal, setOpenModal] = useState(JointAcatTransferConfirmType.NA)

  const { transferAlias } = useParams<{ transferAlias: string }>()

  const { data: wealthOverview, isLoading: isWealthOverviewLoading } = useWealthOverview()

  const {
    data: pendingAcatTransfers,
    isLoading: isPendingAcatTransfersLoading,
    refetch,
  } = useGetPendingJointAcatTransfers({ refetchOnMount: true })

  const pendingAcatTransfer = pendingAcatTransfers?.pendingJointAcatTransfers.find(
    (transfer) => transfer.pendingAcatTransferAlias.toString() === transferAlias,
  )

  const jointAccountHolderFirstName = pendingAcatTransfer?.jointAccountHolderFirstName

  const account = wealthOverview?.personalPortfolio?.accounts.find(
    (account) => account.alias === pendingAcatTransfer?.toAccountAlias,
  )

  const accountName = account?.accountName

  const accountType = account?.__typename

  const { mutate: authorizeAcatTransfer, isLoading: isAuthorizingAcatTransfer } = useAuthorizeJointAcatTransfer({
    onSuccess: (res) => {
      if (res.__typename === 'ReviewJointAcatTransferSuccess' && res.isSuccessful) {
        void setState(PendingJointAcatTransferStatus.AUTHORIZED)
        void refetch()
        setOpenModal(JointAcatTransferConfirmType.NA)
      } else {
        setOpenModal(JointAcatTransferConfirmType.ERROR)
      }
    },
    onError: (error) => {
      log.error(error)
      setOpenModal(JointAcatTransferConfirmType.ERROR)
    },
  })

  const { mutate: rejectAcatTransfer, isLoading: isRejectingAcatTransfer } = useRejectJointAcatTransfer({
    onSuccess: (res) => {
      if (res.__typename === 'ReviewJointAcatTransferSuccess' && res.isSuccessful) {
        void setState(PendingJointAcatTransferStatus.REJECTED)
        void refetch()
        setOpenModal(JointAcatTransferConfirmType.NA)
      } else {
        setOpenModal(JointAcatTransferConfirmType.ERROR)
      }
    },
    onError: (error) => {
      log.error(error)
      setOpenModal(JointAcatTransferConfirmType.ERROR)
    },
  })

  // Set the state based on the pending ACAT transfer status
  useEffect(() => {
    if (!pendingAcatTransfer) return
    setState(pendingAcatTransfer?.status)
  }, [pendingAcatTransfer?.status])

  const modalTitle =
    openModal === JointAcatTransferConfirmType.ACCEPT
      ? t('joint_acat.confirm_authorization')
      : t('joint_acat.reject_authorization')

  const onConfirm = (): void => {
    if (pendingAcatTransfer?.pendingAcatTransferAlias === undefined) return
    if (openModal === JointAcatTransferConfirmType.ACCEPT) {
      void authorizeAcatTransfer({
        pendingAcatTransferAlias: pendingAcatTransfer?.pendingAcatTransferAlias,
        disclosures: [{ id: config.acat.disclosureId, accepted: true }],
      })
    } else {
      rejectAcatTransfer({
        pendingAcatTransferAlias: pendingAcatTransfer?.pendingAcatTransferAlias,
      })
    }
  }

  const onCloseModal = () => {
    setOpenModal(JointAcatTransferConfirmType.NA)
  }

  return (
    <>
      <PageHeader
        backLinkUrl={WEALTH_ROUTES.TRANSFER.path}
        subtitle={t('transfer.title')}
        title={t('tabbed_account_transfer.title')}
      />
      {isPendingAcatTransfersLoading ||
        (isWealthOverviewLoading && (
          <div className="egw-container">
            <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
              <Skeleton count={5} height="50px" />
            </SkeletonTheme>
          </div>
        ))}
      {!isPendingAcatTransfersLoading && !isWealthOverviewLoading && pendingAcatTransfer ? (
        <div className="egw-container">
          {state === PendingJointAcatTransferStatus.REQUIRES_REVIEW && (
            <div className="flex flex-col">
              <p className="typo-primary-base-regular text-body mb-xl">
                {t('joint_acat.pending_p1', { jointAccountHolderFirstName, accountName })}
              </p>

              <TransferReviewSection
                sectionTitle={t('joint_acat.submitted_account_statements')}
                sectionContentPrimary={
                  <div className="flex flex-col">
                    {pendingAcatTransfer?.submittedFileNames.map((fileName) => (
                      <p key={fileName}>{fileName}</p>
                    ))}
                  </div>
                }
              />

              <TransferReviewSection
                sectionTitle={t('joint_acat.to')}
                sectionContentPrimary={accountName}
                sectionContentSecondary={
                  <p className="typo-primary-base-regular text-text-meta">{t(`account_types.${accountType}`)}</p>
                }
              />

              <TransferReviewSection
                sectionTitle={t('joint_acat.transfer_type')}
                sectionContentPrimary={t(`transfer_types.${pendingAcatTransfer?.transferType}`)}
              />

              <div className="flex flex-col gap-sm mt-xl">
                <p className="typo-primary-sm-regular text-text-meta">
                  <Trans t={t} i18nKey="joint_acat.joint_acat_transfer_terms">
                    <a
                      target="_blank"
                      href={config.zendeskArticles.acatTermsAndConditions}
                      className="typo-primary-sm-semibold inline cursor-pointer hover:underline"
                      rel="noopener"
                    >
                      {t('joint_acat.acat_terms_and_conditions')}
                    </a>
                  </Trans>
                </p>
                {/** Only show the authorize and reject buttons if the transfer is in review for current user joint owner */}
                {pendingAcatTransfer?.status === PendingJointAcatTransferStatus.REQUIRES_REVIEW && (
                  <div className="flex flex-row gap-sm">
                    <Button
                      variant={ButtonVariant.Secondary}
                      onClick={() => {
                        setOpenModal(JointAcatTransferConfirmType.REJECT)
                      }}
                    >
                      {t('joint_acat.reject_transfer')}
                    </Button>
                    <Button
                      variant={ButtonVariant.Primary}
                      onClick={() => {
                        setOpenModal(JointAcatTransferConfirmType.ACCEPT)
                      }}
                    >
                      {t('joint_acat.authorize_transfer')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
          {state === PendingJointAcatTransferStatus.REJECTED && (
            <>
              <div className="flex flex-col gap-sm">
                <ExclamationCircleIcon className="size-12 stroke-1 text-text-error mb-sm" />
                <h1 className="typo-primary-xl-light text-text-header-error">
                  {t('joint_acat.transfer_request_rejected')}
                </h1>
                <p className="typo-primary-base-regular text-body">{t(`joint_acat.rejected_p1`)}</p>
              </div>
              <Button
                data-testid="makeAnotherTransfer_button"
                className="w-fit mt-lg"
                variant={ButtonVariant.Secondary}
                onClick={() => {
                  void navigate(WEALTH_ROUTES.TRANSFER.path)
                }}
              >
                {t('common.make_another_transfer')}
              </Button>
            </>
          )}
          {state === PendingJointAcatTransferStatus.AUTHORIZED && (
            <>
              <div className="flex flex-col gap-sm">
                <CheckCircleIcon className="h-[4rem] w-[4rem] stroke-textSuccess stroke-[.5px]" />
                <h1 className="typo-primary-xl-light text-text-header-success">
                  {t('joint_acat.transfer_request_received')}
                </h1>
                <p className="typo-primary-base-regular text-body">{t(`joint_acat.success_p1`)}</p>
                <p className="typo-primary-base-regular text-body">{t(`joint_acat.success_p2`, { accountName })}</p>
              </div>
              <Button
                data-testid="makeAnotherTransfer_button"
                className="w-fit mt-lg"
                variant={ButtonVariant.Secondary}
                onClick={() => {
                  void navigate(WEALTH_ROUTES.TRANSFER.path)
                }}
              >
                {t('common.make_another_transfer')}
              </Button>
            </>
          )}
        </div>
      ) : null}

      <ModalDrawer isOpen={openModal !== JointAcatTransferConfirmType.NA} onClose={onCloseModal} title={modalTitle}>
        <JointAcatTransferConfirmation
          showError={openModal === JointAcatTransferConfirmType.ERROR}
          isLoading={isAuthorizingAcatTransfer || isRejectingAcatTransfer}
          onConfirm={onConfirm}
          onCancel={onCloseModal}
          confirmType={openModal}
        />
      </ModalDrawer>
    </>
  )
}
